import React, { forwardRef } from 'react';
import styled from 'styled-components';
import clsx from 'clsx';
import { rem } from 'polished';
import { Switch as MuiSwitch, SwitchProps as MuiSwitchProps } from '@material-ui/core';

export interface SwitchProps extends MuiSwitchProps {
  trackColor?: string;
  /** Opacidad del riel cuando está activo (por defecto 1). */
  trackCheckedOpacity?: number;
}

const THUMB = rem('20px');
const TRACK_W = rem('50px');
const TRACK_H = rem('28px');
const THUMB_TRAVEL = rem('26px');

/**
 * Contenedor con display:contents para no alterar el layout; el Switch de MUI (withStyles)
 * sigue recibiendo `classes` completos sin reemplazarlos por un objeto parcial.
 */
const SwitchShell = styled.div<{
  $trackColor?: string;
  $trackCheckedOpacity: number;
}>`
  display: contents;

  & .MuiSwitch-root {
    width: ${TRACK_W};
    height: ${TRACK_H};
    padding: ${rem('2px')};
    overflow: visible;
  }

  & .MuiSwitch-switchBase {
    padding: ${rem('6px')} ${rem('4px')};
    color: ${p => p.theme.colors.Mi[400]};

    &.Mui-checked {
      padding: ${rem('6px')} 0;
      transform: translateX(${THUMB_TRAVEL});
      color: ${p =>
        p.$trackColor !== undefined && p.$trackColor !== ''
          ? p.$trackColor
          : p.theme.colors.Mi[400]};
    }

    &.Mui-disabled {
      color: ${p => p.theme.colors.S[400]};
    }
  }

  & .MuiSwitch-thumb {
    width: ${THUMB};
    height: ${THUMB};
    color: white;
    box-shadow: ${rem('1px')} ${rem('2px')} ${rem('4px')} rgba(0, 0, 0, 0.18);
  }

  & .MuiSwitch-track {
    border-radius: ${rem('14px')};
    opacity: 1;
    background-color: #d1d5db;
    height: ${rem('24px')};
    margin-top: ${rem('2px')};
  }

  & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    background-color: ${p =>
      p.$trackColor !== undefined && p.$trackColor !== ''
        ? p.$trackColor
        : p.theme.colors.Mi[400]};
    opacity: ${p => p.$trackCheckedOpacity};
  }

  & .MuiSwitch-root.Mui-disabled .MuiSwitch-track {
    opacity: 0.45;
  }

  & .MuiSwitch-root.Mui-disabled .MuiSwitch-thumb {
    opacity: 0.85;
  }
`;

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { trackColor, trackCheckedOpacity = 1, className, disabled, classes, ...props },
  ref
) {
  const showDisabledClass = Boolean(disabled);

  return (
    <SwitchShell
      $trackColor={trackColor}
      $trackCheckedOpacity={trackCheckedOpacity}
    >
      <MuiSwitch
        ref={ref}
        disabled={disabled}
        classes={{
          ...(classes ?? {}),
          root: clsx(classes?.root, className, showDisabledClass && 'Mui-disabled')
        }}
        {...props}
      />
    </SwitchShell>
  );
});

export default Switch;
